import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import UserPickerList from '../users/UserPickerList';
import { fetchUsersMeta } from '../users/api';
import type { AdminUser, BulkRowResult, UsersMeta } from '../users/types';
import { fieldClass, labelClass, primaryBtnClass } from '../styles';
import { bulkOrgMembershipAction } from './api';

type MembershipAction = 'give_membership' | 'remove_membership';

const OrganizationUserManagement: React.FC = () => {
  const [meta, setMeta] = useState<UsersMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [listKey, setListKey] = useState(0);
  const [action, setAction] = useState<MembershipAction>('give_membership');
  const [organizationId, setOrganizationId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [results, setResults] = useState<BulkRowResult[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setMetaLoading(true);
      setMetaError(null);
      try {
        const data = await fetchUsersMeta();
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) {
          setMetaError(
            err instanceof Error ? err.message : 'Failed to load user admin data'
          );
        }
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const apply = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one user.');
      return;
    }
    if (!organizationId) {
      setError('Choose an organization.');
      return;
    }
    if (action === 'remove_membership') {
      if (
        !window.confirm(
          `Remove membership for ${selectedIds.length} user(s) from this organization?`
        )
      ) {
        return;
      }
    }
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const payloadRes = await bulkOrgMembershipAction({
        user_ids: selectedIds,
        organization_id: organizationId,
        action,
      });
      setResults(payloadRes.results);
      setSummary(
        `Updated ${payloadRes.updated}` +
          (payloadRes.errors ? `, ${payloadRes.errors} could not be updated.` : '.')
      );
      setListKey((n) => n + 1);
      setSelectedIds([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Membership update failed');
    } finally {
      setBusy(false);
    }
  };

  if (metaLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (metaError || !meta) {
    return (
      <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">
        {metaError || 'Failed to load user admin data'}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-[13px] text-[#626262]">
        Give or remove organization memberships. Visible users follow the same Client / Catalyx
        rules as Bulk Edit User.
      </p>
      <UserPickerList
        meta={meta}
        mode="multi"
        selectedIds={selectedIds}
        refreshToken={listKey}
        onChange={(ids, _users: AdminUser[]) => {
          setSelectedIds(ids);
        }}
      />

      <div className="max-w-3xl border-t border-[#E8E8E8] pt-5">
        <h3 className="text-base font-semibold text-[#0C0B0B] mb-3">Membership action</h3>
        {error && (
          <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
        )}
        {summary && (
          <div className="mb-3 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
            {summary}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as MembershipAction)}
              className={fieldClass}
            >
              <option value="give_membership">Give membership</option>
              <option value="remove_membership">Remove membership</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Organization</label>
            <select
              value={organizationId}
              onChange={(e) =>
                setOrganizationId(e.target.value ? Number(e.target.value) : '')
              }
              className={fieldClass}
            >
              <option value="">Select an organization</option>
              {meta.organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            type="button"
            disabled={busy || selectedIds.length === 0}
            onClick={() => void apply()}
            className={primaryBtnClass}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
            Apply to {selectedIds.length || 0} user{selectedIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>

      {results && (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Email</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr key={`${row.id}-${row.email}`} className="border-t border-[#E8E8E8]">
                  <td className="px-3 py-2">{row.email || '—'}</td>
                  <td className="px-3 py-2 capitalize">{row.status}</td>
                  <td
                    className={`px-3 py-2 ${
                      row.status === 'error' ? 'text-red-700' : 'text-[#4A4A4A]'
                    }`}
                  >
                    {row.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrganizationUserManagement;
